import {
  PaginationState,
  SortingState,
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import Link from "next/link";
import { useCallback, useState } from "react";
import { twMerge } from "tailwind-merge";

import { OverflowTooltip } from "@/components/overflow-tooltip";
import { links } from "@/data/links";
import { useLang } from "@/hooks/use-lang";
import { toTitleCase } from "@/utils/to-title-case";

import { ArrowRight } from "ui/icon/shared";

import {
  HoldingsInfo,
  HoldingsInfoCategory,
  HoldingsInfoData,
  useInvestorHoldingsInfo,
} from "../services/get-investor-holdings-info";

export const useHoldingsInfoTable = ({
  investor,
  category,
  initialData,
}: {
  investor: string;
  category: HoldingsInfoCategory;
  initialData?: HoldingsInfo;
}) => {
  const { t, lang } = useLang();
  const columnHelper = createColumnHelper<HoldingsInfoData>();
  const columns = [
    columnHelper.accessor("symbol", {
      header: () => (
        <Header className="whitespace-nowrap pl-2">{t.my13F.symbol}</Header>
      ),
      cell: (info) => (
        <Link
          target="_blank"
          className="w-full whitespace-nowrap pl-2 font-medium text-sky-700 underline underline-offset-2"
          href={`/${lang}${links.myAnalysisOverview({
            symbol: info.getValue() ?? "",
          })}`}
        >
          {info.getValue()}
        </Link>
      ),
      enableSorting: false,
    }),
    columnHelper.accessor("company", {
      header: () => (
        <Header className="max-w-[140px] whitespace-nowrap">
          {t.my13F.company}
        </Header>
      ),
      cell: (info) => (
        <OverflowTooltip
          wrapperClassName="font-normal max-w-[140px]"
          title={toTitleCase(info.getValue() ?? "")}
        >
          {toTitleCase(info.getValue() ?? "")}
        </OverflowTooltip>
      ),
    }),
    columnHelper.accessor("sectorCategory", {
      header: () => (
        <Header className="max-w-[120px] whitespace-nowrap">
          {t.my13F.sector}
        </Header>
      ),
      cell: (info) => (
        <OverflowTooltip
          className="max-w-[120px]"
          title={toTitleCase(info.getValue() ?? "")}
        >
          {toTitleCase(info.getValue() ?? "")}
        </OverflowTooltip>
      ),
    }),
    columnHelper.accessor("weightPct", {
      header: () => (
        <Header className="whitespace-nowrap">
          {t.my13F.investor.proportion}
        </Header>
      ),
      cell: (info) => {
        const value = info.getValue();
        return (
          <p className="text-sm font-medium">
            {value ? `${+value?.toFixed(2)}%` : "-"}
          </p>
        );
      },
      meta: { align: "right" },
    }),
    columnHelper.accessor("amount", {
      header: () => (
        <Header className="whitespace-nowrap">
          {t.my13F.investor.holdingAmount}
        </Header>
      ),
      cell: (info) => (
        <p className="text-sm font-medium">
          ${info.getValue()?.toLocaleString()}
        </p>
      ),
      meta: { align: "right" },
    }),
    columnHelper.accessor("units", {
      header: () => (
        <Header className="whitespace-nowrap">
          {t.my13F.investor.holdingShares}
        </Header>
      ),
      cell: (info) => (
        <p className="text-sm font-medium">
          {info.getValue()?.toLocaleString()}
        </p>
      ),
      meta: { align: "right" },
    }),
    columnHelper.accessor("unitsPct", {
      header: () => (
        <Header className="whitespace-nowrap pr-2">
          {t.my13F.investor.holdingChange}
        </Header>
      ),
      cell: (info) => {
        const value = info.getValue();
        if (value == null) return <p className="pr-2 text-sm font-medium">-</p>;

        const isRise =
          (typeof value === "string" && ["New Buy", "建倉"].includes(value)) ||
          (typeof value === "number" && value > 0);
        const isFall =
          (typeof value === "string" && ["Sold Out", "清倉"].includes(value)) ||
          (typeof value === "number" && value < 0);

        return (
          <div className="flex w-full items-center justify-end gap-1 px-1">
            <p
              className={twMerge(
                "text-sm font-medium",
                isRise && "text-emerald-500",
                isFall && "text-red-500"
              )}
            >
              {getUnitsPctDisplay(value)}
            </p>
            {typeof value === "number" && value !== 0 && (
              <ArrowRight
                className={twMerge(
                  "mt-px",
                  isRise && "-rotate-45 fill-emerald-500 stroke-emerald-500",
                  isFall && "rotate-45 fill-red-500 stroke-red-500"
                )}
              />
            )}
          </div>
        );
      },
      meta: { align: "right" },
    }),
  ];

  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const { data, isFetching } = useInvestorHoldingsInfo({
    investor,
    category,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    sortBy: sorting[0]?.id
      ? `${sorting[0]?.id}${sorting[0]?.desc ? 0 : 1}`
      : undefined,
    initialData:
      pagination.pageIndex === 0 &&
      pagination.pageSize === 10 &&
      category === "all" &&
      sorting[0]?.id == null
        ? initialData
        : undefined,
  });

  const resetTableState = useCallback(() => {
    setSorting([]);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  return {
    table: useReactTable({
      data: data?.data ?? [],
      columns,
      state: {
        sorting,
        pagination,
      },
      pageCount: Math.ceil((data?.total ?? 0) / pagination.pageSize),
      getCoreRowModel: getCoreRowModel(),
      manualSorting: true,
      manualPagination: true,
      onSortingChange: setSorting,
      onPaginationChange: setPagination,
    }),
    isFetching,
    resetTableState,
  };
};

const Header = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={twMerge("font-medium text-slate-500", className)}>
    {children}
  </div>
);

const getUnitsPctDisplay = (value: number | string) => {
  if (typeof value !== "number") return value;
  if (Math.abs(value) >= 1) return `${+value.toFixed(0)}%`;
  return `${value === 0 ? "0" : value > 0 ? "0~1" : "-1~0"}%`;
};
